import { useEffect, useMemo, useState } from "react";
import { fetchContent } from "@/lib/api";
import { FadeUp, RevealImage } from "@/components/Reveal";
import Lightbox from "@/components/Lightbox";
import { Lotus } from "@/components/Motifs";

export default function FolkArt() {
  const [artworks, setArtworks] = useState([]);
  const [active, setActive] = useState("All");
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    fetchContent("/artworks").then(setArtworks);
  }, []);

  const categories = useMemo(
    () => ["All", ...new Set(artworks.map((a) => a.category))],
    [artworks]
  );
  const visible = active === "All" ? artworks : artworks.filter((a) => a.category === active);

  return (
    <main data-testid="folk-art-page" className="pt-24">
      <section className="max-w-[1500px] mx-auto px-5 md:px-10 pt-10 md:pt-16 pb-14">
        <FadeUp>
          <p className="text-[11px] tracking-[0.3em] uppercase text-alta mb-6">The Gallery</p>
          <h1 className="font-serif font-light text-5xl sm:text-6xl lg:text-7xl tracking-tight text-ink">
            Stories Painted by Hand
          </h1>
          <p className="mt-6 max-w-xl text-base font-light text-ink/65 leading-relaxed">
            Paintings, patachitra-inspired works, textile art and sketches — the archive from which every collection is drawn.
          </p>
        </FadeUp>

        <FadeUp delay={0.1} className="mt-12 flex flex-wrap gap-3">
          {categories.map((cat) => (
            <button
              key={cat}
              data-testid={`filter-${cat.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => setActive(cat)}
              className={`px-5 py-2.5 text-[10px] font-medium tracking-[0.22em] uppercase border transition-all duration-500 ${
                active === cat
                  ? "bg-ink text-paper border-ink"
                  : "border-ink/25 text-ink/60 hover:border-ink hover:text-ink"
              }`}
            >
              {cat}
            </button>
          ))}
        </FadeUp>
      </section>

      <section className="max-w-[1500px] mx-auto px-5 md:px-10 pb-28">
        {visible.length === 0 ? (
          <p className="text-ink/50 font-light py-16" data-testid="artworks-empty">The gallery walls are being hung…</p>
        ) : (
          <div className="columns-1 sm:columns-2 lg:columns-3 gap-6 [column-fill:_balance]" data-testid="artworks-masonry">
            {visible.map((a, i) => (
              <FadeUp key={a.id} delay={(i % 3) * 0.07} className="mb-6 break-inside-avoid">
                <button
                  data-testid={`artwork-card-${a.id}`}
                  data-cursor="VIEW"
                  onClick={() => setLightbox(a)}
                  className="group block w-full text-left"
                >
                  <RevealImage
                    src={a.image}
                    alt={a.title}
                    className={i % 4 === 0 ? "aspect-[3/4]" : i % 4 === 1 ? "aspect-square" : i % 4 === 2 ? "aspect-[4/5]" : "aspect-[3/3.6]"}
                  />
                  <div className="mt-4 flex items-start justify-between gap-3">
                    <div>
                      <h3 className="font-serif text-xl md:text-2xl text-ink group-hover:text-alta transition-colors duration-500">{a.title}</h3>
                      <p className="mt-1 text-[10px] tracking-[0.2em] uppercase text-ink/45">
                        {a.category} · {a.medium}{a.year ? ` · ${a.year}` : ""}
                      </p>
                    </div>
                    <Lotus className="w-5 h-5 text-terracotta/40 shrink-0 mt-1 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  </div>
                </button>
              </FadeUp>
            ))}
          </div>
        )}
      </section>

      <Lightbox item={lightbox} onClose={() => setLightbox(null)} />
    </main>
  );
}
